import type { CSSProperties, SVGProps } from "react";
import { ICONS, type IconId } from "@caliu/styles/icons/data";

export type { IconId };

export interface IconProps extends Omit<SVGProps<SVGSVGElement>, "name" | "children"> {
  /** Espurna icon id (see packages/styles/icons/catalog.json). */
  name: IconId;
  /** Rendered width and height in px (scaled up on phones, see index.css). Defaults to 24. */
  size?: number;
}

/** Stroke weight per rendered size, so small icons hold up (Espurna README, "Stroke"). */
function strokeFor(size: number): number {
  if (size <= 16) return 1.9;
  if (size <= 20) return 1.75;
  return 1.65;
}

/**
 * Renders an Espurna icon. Colour comes from `currentColor`, so style it with
 * text tokens (`text-muted`, `text-accent`…), never with fill/stroke props.
 * Decorative by default: pass `aria-label` (or label the parent control).
 */
export function Icon({ name, size = 24, ...rest }: IconProps) {
  const icon = ICONS[name];
  const labelled = rest["aria-label"] !== undefined || rest["aria-labelledby"] !== undefined;
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden={labelled ? undefined : true}
      role={labelled ? "img" : undefined}
      focusable="false"
      data-icon={name}
      {...rest}
      // index.css draws the icon at this size, and larger on phones.
      style={{ "--icon-size": `${size}px`, ...rest.style } as CSSProperties}
    >
      <g
        stroke="currentColor"
        strokeWidth={strokeFor(size)}
        strokeLinecap="round"
        strokeLinejoin="round"
        dangerouslySetInnerHTML={{ __html: icon.line }}
      />
    </svg>
  );
}
